import { DialogContent, DialogTitle, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { paperMaterialBusiness } from "api/bussiness/paper-material/paper-material-bussiness";
import { invalidatePaperCatalogCache } from "api/cached";
import { PaperTypeEnum } from "commons/enum/paper-type";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { observer } from "mobx-react-lite";
import { IUpdatePaperMaterial } from "mobx-stores/_models/paper-inventory/paper-material";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import {
  getPaperSpecificationTypeOptionLabel,
  getPaperTypeOptionLabel,
} from "../share";
import { getUpdatePaperMaterialSchema } from "./schema";

export const UpdatePaperMaterial = observer(() => {
  const queryClient = useQueryClient();
  const {
    paperMaterialStore: {
      openEdit,
      getPapersMaterial,
      paperMaterialDetail,
      pagination,
      setProp,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    submitHandler,
  } = useForm<IUpdatePaperMaterial>(getUpdatePaperMaterialSchema(), {
    id: paperMaterialDetail?.id,
    code: paperMaterialDetail?.code,
    gsm: paperMaterialDetail?.gsm,
    specificationLength: paperMaterialDetail?.specificationLength,
    specificationSize: paperMaterialDetail?.specificationSize,
    specificationType: paperMaterialDetail?.specificationType,
    type: paperMaterialDetail?.type,
    typeSelect: paperMaterialDetail?.typeSelect || PaperTypeEnum.Other,
    specificationTypeSelect:
      paperMaterialDetail?.specificationTypeSelect || PaperTypeEnum.Other,
  });

  const saveHandler = useCallback(
    async (data: IUpdatePaperMaterial) => {
      try {
        if (!paperMaterialDetail?.id) return;
        await paperMaterialBusiness.update(paperMaterialDetail?.id, data);
        await invalidatePaperCatalogCache(queryClient);
        await getPapersMaterial({
          filter: pagination.filter,
          maxResultCount: pagination.itemsPerPage,
          skipCount: pagination.skipCount,
          sorting: "",
        });
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [
      paperMaterialDetail?.id,
      getPapersMaterial,
      pagination.filter,
      pagination.itemsPerPage,
      pagination.skipCount,
      queryClient,
      setProp,
    ],
  );

  const handlePaperTypeSelectChanged = useCallback(
    (control: IControl) => {
      const typeCtr = controls.find((c) => c.name === "type");
      if (typeCtr) {
        if (control.value === PaperTypeEnum.Other) {
          typeCtr.setProp("disabled", false);
          typeCtr.setValueAndValidate("");
        } else {
          typeCtr.setProp("disabled", true);
          typeCtr.setValueAndValidate(getPaperTypeOptionLabel(control.value));
        }
      }
    },
    [controls],
  );

  const handlePaperSpecificationTypeChanged = useCallback(
    (control: IControl) => {
      const specificationTypeCtr = controls.find(
        (c) => c.name === "specificationType",
      );
      if (specificationTypeCtr) {
        if (control.value === PaperTypeEnum.Other) {
          specificationTypeCtr.setProp("disabled", false);
          specificationTypeCtr.setValueAndValidate("");
        } else {
          specificationTypeCtr.setProp("disabled", true);
          specificationTypeCtr.setValueAndValidate(
            getPaperSpecificationTypeOptionLabel(control.value),
          );
        }
      }
    },
    [controls],
  );

  const valuesChangedHandler = useCallback(
    (control: IControl) => {
      if (control.name === "typeSelect") {
        handlePaperTypeSelectChanged(control);
      }
      if (control.name === "specificationTypeSelect") {
        handlePaperSpecificationTypeChanged(control);
      }
    },
    [handlePaperSpecificationTypeChanged, handlePaperTypeSelectChanged],
  );

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"sm"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa vật tư giấy</DialogTitle>
      <DialogContent>
        <Stack sx={{ marginTop: "10px" }}>
          <DynamicForm
            controls={controls}
            onValueChanged={valuesChangedHandler}
          ></DynamicForm>
        </Stack>
      </DialogContent>
    </DialogForm>
  );
});

